import { useQuery } from '@apollo/client';
import type { AccessType } from '@risksmart-app/domain/src/types/consts/access-type';
import type { ContributorType } from '@risksmart-app/domain/src/types/consts/contributor-type';
import type { ParentType } from '@risksmart-app/domain/src/types/consts/parent-type';
import { GetRoleAccessDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

type RoleAccess = {
  __typename?: 'role_access';
  AccessType: AccessType;
  ContributorType: ContributorType;
  ObjectType: ParentType;
}[];

/**
 * Bridge hook for the `role_access` catalog that backs `PermissionsProvider`.
 *
 * This wraps a little more than the usual `createQueryHook` bridge because the
 * V3 (`trpc`) path has no `getRoleAccess` tRPC equivalent — under V3 the catalog
 * is no longer consulted at all. Permission decisions are resolved server-side
 * via the perm_v2 batch endpoints (see `useHasPermissionQuery` /
 * `useHasPermissionLazy`), so the legacy `getRoleAccess` read is skipped and an
 * empty (non-null) catalog is returned — enough to satisfy the consumers'
 * presence guard without firing a dead query. The legacy (`trpc` off) path
 * fetches the catalog via Apollo exactly as before.
 */
export const useGetRoleAccess = (): {
  roleAccess: RoleAccess | undefined;
  loading: boolean;
  error: boolean;
} => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const { data, loading, error } = useQuery(GetRoleAccessDocument, {
    skip: trpcEnabled,
  });

  if (trpcEnabled) {
    return { roleAccess: [], loading: false, error: false };
  }

  return { roleAccess: data?.role_access, loading, error: !!error };
};
